import { useContext, useMemo, useState } from 'react';
import {
  DataGrid,
  GridActionsCellItem,
  type GridColDef,
  type GridRowSelectionModel
} from '@mui/x-data-grid';
import { Box, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Unarchive';
import moment from 'moment';
import type { User, Role, Group } from '@esg/esg-global-types';
import DeleteUser from './DeleteUser';
import EditUserDrawer from './EditUserDrawer';
import { UserContext } from '../../context/UserContext';
import { canManageUser, getUserTypes, getVisibleUserRows } from '../../util/user_access';
import { UsersTableToolbar } from './UsersTableToolbar';
import { nowISOString } from '../../util/date';
import DataGridNoRows from '../shared/datagrid/DataGridNoRows';
import DataGridLoading from '../shared/datagrid/DataGridLoading';
import RestoreConfirmModal from '../shared/modal/RestoreConfirmModal';
import { user_label } from '../../lib/user_management/users';
import { useUsersTable } from './hooks/useUsersTable';
import { useUserMutations } from './hooks/useUserMutations';
import StatusChip from './StatusChip';
import CompanyChipStack from './CompanyChipStack';
import BulkActionBar from './BulkActionBar';
interface UsersTableProps {
  /**
   * Groups scoped to the viewer's admin reach — drives the toolbar filters, the
   * edit drawer selectors and the bulk "add to company" menu so a group admin
   * only ever operates on groups they administer.
   */
  groups: Array<Group>;
  /**
   * Unscoped group list, used only to resolve company/group names for the
   * Companies column chips (a scoped list can't name out-of-scope entities).
   */
  all_groups: Array<Group>;
  roles: Array<Role>;
  handleCreateUser(): void;
  /**
   * Bumped by the host after an out-of-band mutation (Add User, CSV import)
   * that should jump the list back to page 1 so the change is visible under
   * the default created-DESC ordering.
   */
  refreshSignal?: number;
  /** Opens the CSV import wizard. */
  onOpenImportCsv?: () => void;
  /** When provided, renders an "Assign to company" action in the toolbar. */
  onAssignToCompany?: () => void;
}

/** Datagrid handling the display of User data. */
const UsersTable = ({
  groups,
  all_groups,
  roles,
  handleCreateUser,
  refreshSignal,
  onOpenImportCsv,
  onAssignToCompany
}: UsersTableProps) => {
  const { user } = useContext(UserContext);
  const mutations = useUserMutations();

  const [actionRow, setActionRow] = useState<User | null>(null);
  const [showEditDrawer, setShowEditDrawer] = useState<boolean>(false);
  const [showDeleteDrawer, setShowDeleteDrawer] = useState<boolean>(false);
  const [showRestoreModal, setShowRestoreModal] = useState<boolean>(false);

  const {
    filters,
    setFilter,
    rows,
    loading,
    hasNextPage,
    hasPrevPage,
    pageIndex,
    goToNextPage,
    goToPrevPage,
    refetchCurrentPage,
    patchRow,
    selection,
    setSelectionFromIds,
    clearSelection
  } = useUsersTable({ refreshSignal });

  // Defence-in-depth: the server already scopes a group admin's results
  // (see getUsersPaginated), so this is never expected to filter anything out
  // in practice — kept as a cheap client-side backstop against a future
  // server-side regression, mirroring the pre-existing check.
  const visibleRows: Array<User> = useMemo(() => getVisibleUserRows(user, rows), [user, rows]);

  const handleOpenEdit = (row: User): void => {
    setActionRow(row);
    setShowEditDrawer(true);
  };
  const handleOpenDelete = (row: User): void => {
    setActionRow(row);
    setShowDeleteDrawer(true);
  };
  const handleOpenRestore = (row: User): void => {
    setActionRow(row);
    setShowRestoreModal(true);
  };

  const handleResendInvite = async (userId: string): Promise<void> => {
    const row = visibleRows.find((r) => r.id === userId);
    if (!row) return;
    await mutations.resendInvite({ name: row.first_name, email: row.email });
    // Refetch rather than patch. The chip now derives from `welcome_email_sent_at`
    // and `welcome_email_kind`, and neither is knowable here:
    //   - the welcome-email function picks the template from the address's
    //     domain, so the client cannot say whether this was `set_up` or
    //     `sso_notice` — and an SSO user nothing has been sent to yet derives
    //     `not_invited`, so the button really can reach that case;
    //   - `resendInvite` reports failures by toast and resolves either way, so
    //     an optimistic patch would flip the chip on a send that never left,
    //     hiding the very action needed to retry.
    // The function awaits its own recording before responding, so by the time
    // the call above resolves the fields are already written — no race.
    refetchCurrentPage();
  };

  const handleSingleDeleteFromDrawer = (deleted_user: User): void => {
    patchRow(deleted_user.id, () => ({ ...deleted_user, deleted: nowISOString() }) as User);
  };

  const handleRestoreFromModal = async (target: User): Promise<void> => {
    await mutations.restoreUser(target.id);
    patchRow(target.id, () => ({ ...target, deleted: null }) as User);
    setShowRestoreModal(false);
    setActionRow(null);
  };

  const columns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      sortable: false,
      renderCell: (params) => `${params.row.first_name} ${params.row.last_name}`.trim()
    },
    { field: 'email', headerName: 'Email', flex: 1.4 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1.2,
      sortable: false,
      renderCell: (params) => (
        <StatusChip user={params.row as User} onResendInvite={handleResendInvite} />
      )
    },
    {
      field: 'companies',
      headerName: 'Companies',
      flex: 1.5,
      sortable: false,
      renderCell: (params) => (
        <CompanyChipStack user={params.row as User} groups={all_groups} current_user={user} />
      )
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 0.8,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as User;
        if (row.super_admin) return 'Super admin';
        const rolesPresent = Array.from(
          new Set(
            Object.values(row.groups ?? {})
              .flatMap((g) => Object.values(g.companies ?? {}))
              .map((c) => c.role)
          )
        );
        if (rolesPresent.length === 0) {
          const adminGroups = Object.values(row.groups ?? {}).filter((g) => g.admin);
          return adminGroups.length > 0 ? 'Group admin' : '—';
        }
        if (rolesPresent.length === 1) {
          return rolesPresent[0].charAt(0).toUpperCase() + rolesPresent[0].slice(1);
        }
        return 'Mixed';
      }
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.7,
      sortable: false,
      renderCell: (params) => {
        const match = getUserTypes().find((t) => t.id === params.row.type);
        return match?.name ?? params.row.type;
      }
    },
    {
      field: 'last_accessed',
      headerName: 'Last active',
      flex: 1,
      sortable: false,
      renderCell: (params) =>
        params.row.last_accessed ? moment(params.row.last_accessed).fromNow() : '—'
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      flex: 0.6,
      align: 'right',
      getActions: ({ row }) => {
        const canManage = canManageUser(user, row as User);
        if (row.deleted) {
          return [
            <Tooltip title={canManage ? 'Restore user' : "You can't restore this user"} key="r">
              <span>
                <GridActionsCellItem
                  disabled={!canManage}
                  icon={<RestoreIcon fontSize="small" />}
                  label="Restore"
                  onClick={() => handleOpenRestore(row as User)}
                />
              </span>
            </Tooltip>
          ];
        }
        return [
          <Tooltip title={canManage ? 'Edit user' : "You can't edit this user"} key="e">
            <span>
              <GridActionsCellItem
                disabled={!canManage}
                icon={<EditIcon fontSize="small" />}
                label="Edit"
                onClick={() => handleOpenEdit(row as User)}
              />
            </span>
          </Tooltip>,
          <Tooltip title={canManage ? 'Delete user' : "You can't delete this user"} key="d">
            <span>
              <GridActionsCellItem
                disabled={!canManage}
                icon={<DeleteIcon fontSize="small" />}
                label="Delete"
                onClick={() => handleOpenDelete(row as User)}
              />
            </span>
          </Tooltip>
        ];
      }
    }
  ];

  return (
    <>
      <RestoreConfirmModal
        open={showRestoreModal}
        handleCloseRestoreModal={() => {
          setShowRestoreModal(false);
          setActionRow(null);
        }}
        restore_explainer={
          'This user will have all of their old permissions and will be able to sign in using their old email.'
        }
        restore_label={actionRow && actionRow.email}
        handleRestore={actionRow && (() => handleRestoreFromModal(actionRow))}
      />

      <Drawer
        anchor="right"
        open={showEditDrawer}
        onClose={() => setShowEditDrawer(false)}
        PaperProps={{ style: { width: '60%' } }}
      >
        {actionRow && (
          <EditUserDrawer
            user={actionRow}
            groups={groups}
            roles={roles}
            onSaved={(updated) => patchRow(updated.id, () => updated)}
            onClose={() => {
              setActionRow(null);
              setShowEditDrawer(false);
            }}
          />
        )}
      </Drawer>

      <Drawer
        anchor="right"
        open={showDeleteDrawer}
        onClose={() => setShowDeleteDrawer(false)}
        PaperProps={{ style: { width: '30%' } }}
      >
        <DeleteUser
          delete_user={actionRow}
          users={visibleRows}
          handleDeleteUserRow={handleSingleDeleteFromDrawer}
          closeDrawer={() => {
            setActionRow(null);
            setShowDeleteDrawer(false);
          }}
        />
      </Drawer>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <UsersTableToolbar
          filters={filters}
          setFilter={setFilter}
          groups={groups}
          roles={roles}
          handleAddSingle={handleCreateUser}
          handleImportCsv={onOpenImportCsv ?? (() => undefined)}
          onAssignToCompany={onAssignToCompany}
        />

        <BulkActionBar
          selectedUsers={[...selection.values()]}
          groups={groups}
          onAfterAction={refetchCurrentPage}
          onClear={clearSelection}
        />

        <DataGrid
          autoHeight
          loading={loading}
          rows={visibleRows}
          columns={columns}
          checkboxSelection
          keepNonExistentRowsSelected
          rowSelectionModel={[...selection.keys()] as GridRowSelectionModel}
          onRowSelectionModelChange={(model) => setSelectionFromIds(model.map(String))}
          paginationMode="server"
          // Cursor pagination has no total count to report — -1 is MUI's
          // documented "unknown row count" sentinel. The footer's own
          // pagination UI is hidden anyway (hideFooterPagination); this only
          // stops the "server mode without rowCount" dev warning.
          rowCount={-1}
          hideFooterPagination
          disableRowSelectionOnClick
          slots={{
            noRowsOverlay: () => <DataGridNoRows missing_label={user_label} />,
            loadingOverlay: () => <DataGridLoading columns={columns.length} />
          }}
          getRowClassName={(params) =>
            `users-grid-row-${params.row.deleted ? 'deleted' : 'enabled'}`
          }
          disableColumnSelector
          sx={{
            '& .MuiDataGrid-row': { boxSizing: 'border-box' },
            '& .users-grid-row-deleted': {
              fontStyle: 'italic',
              color: 'rgba(66, 66, 66, 0.8)',
              backgroundColor: 'rgba(66, 66, 66, 0.05)'
            },
            '&, [class^=MuiDataGrid]': { border: 'none' }
          }}
        />

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ px: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            Page {pageIndex + 1}
          </Typography>
          <IconButton
            size="small"
            onClick={goToPrevPage}
            disabled={!hasPrevPage || loading}
            aria-label="Previous page"
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={goToNextPage}
            disabled={!hasNextPage || loading}
            aria-label="Next page"
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
      </Box>
    </>
  );
};

export default UsersTable;
